import {Component, Injector} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {BaseConfigurableComponent} from '@twpub/shared/components/base';
import {ResultListProp, SearchResultConfig} from '@twpub/core/constants';
import {
  ConceptService,
  DictionaryService,
  NavigationService,
  SearchService,
  SharedStateService
} from '@twpub/core/services';
import {
  ConceptSelection,
  Language,
  PaginationData,
  SearchCriteria,
  SessionObject,
  Term,
  TermSearchResult
} from '@twpub/core/models';
import {fromPageEvent} from '@twpub/core/utils';
import {environment} from '../../../../../environments/environment';
import _ from 'lodash';

@Component({
  template: '',
  standalone: false
})
export abstract class BaseResultListComponent extends BaseConfigurableComponent {
  getSearchTerm = () => {
    return this.sharedStateService.getSearchTerm();
  }

  private currentSearch?: SearchCriteria;
  private currentPData?: PaginationData;

  get searchConfig(): SearchResultConfig {
    if (!this._searchConfig) {
      this.onInit();
    }
    return this._searchConfig as SearchResultConfig;
  }

  protected searchService: SearchService;
  dictService: DictionaryService
  protected conceptService: ConceptService;
  protected naviService: NavigationService;
  sharedStateService: SharedStateService;
  resultPaginator!: PaginationData;
  results?: TermSearchResult[];

  dataSource: MatTableDataSource<TermSearchResult> = new MatTableDataSource();
  isSearching = false;
  isLoading = false;
  private _searchConfig?: SearchResultConfig
  includeSynonyms = false; // endast AF
  protected languages: Language[] = [];

  dictIds?: number[];
  sourceLangCodes: string[] = [];
  protected targetLangCodes: string[] = [];
  /**
   * Explicitly casts the type of the result object to avoid "Unresolved variable" errors in template.
   * @param res - TermSearchResult object from MatTableDataSource
   */
  toRes = (res: any): TermSearchResult => res as TermSearchResult;

  hasMultipleSourceLangs = () => this.sourceLangCodes.length !== 1;

  isSourceLang = (code: string) => this.sourceLangCodes.includes(code);
  isLanguage = (code: string) => this.languages.some(l => l.code === code);

  protected constructor(injectorObj: Injector) {
    super();
    this.naviService = injectorObj.get(NavigationService);
    this.searchService = injectorObj.get(SearchService);
    this.dictService = injectorObj.get(DictionaryService);
    this.conceptService = injectorObj.get(ConceptService);
    this.sharedStateService = injectorObj.get(SharedStateService);
  }

  performSearch(pData?: PaginationData, clear: boolean = false, callbackFn?: (resultIndex?: number) => void) {
    const sourceLangs = this.sharedStateService.getSourceLangs();
    const shouldPerformSearch = this.shouldPerformSearch();
    if (shouldPerformSearch && sourceLangs) {
      const searchCriteria = new SearchCriteria(this.sharedStateService.sessionObj);
      if (searchCriteria.equals(this.currentSearch) && _.isEqual(pData, this.currentPData)) return;
      this.logger.trace('BaseResultListComponent.performSearch searching:', {searchCriteria})
      this.currentSearch = searchCriteria;
      this.currentPData = pData;
      this.isLoading = true;
      this.searchService.search(searchCriteria, this.searchConfig, pData).subscribe({
        next: (pagedResult) => {
          if (clear || this.results === undefined) {
            this.results = [];
          }
          this.results.push(...pagedResult.results);
          this.dataSource.data = this.results; // todo move to child class. Only relevant for mat-table
          this.logger.trace('BaseResultListComponent.performSearch results: ', {res: this.results});
          !environment.production && this.validateSortKeys();
          this.sharedStateService.setPaginationData(fromPageEvent({
            pageSize: pagedResult.pageSize,
            pageIndex: pData?.pageIndex || 0,
            previousPageIndex: pData?.previousPageIndex || 0,
            length: pagedResult.total
          }));
          this.isSearching = true;
          this.isLoading = false;
          this.resultPaginator = this.sharedStateService.getPaginationData();
          this.sharedStateService.setPaginationData(this.resultPaginator);
          if (callbackFn) {
            callbackFn(this.sharedStateService.getResultIndex());
          }
          this.openDetailsForSingleResult();
        },
        error: (err) => {
          this.logger.error('BaseResultListComponent.performSearch error:', err);
          this.isLoading = false;
        }
      });
    } else {
      this.results = [];
      this.dataSource.data = [];
      this.isSearching = false;
      this.isLoading = false;
      this.currentSearch = undefined;
      this.currentPData = undefined;
    }
  }

  private shouldPerformSearch() {
    const initialSearch = this.config.getBoolean(ResultListProp.InitialSearch);
    const searchTerm = this.sharedStateService.getSearchTerm();
    const activeFilters = this.sharedStateService.getFieldFilters()?.some(f => f.values?.length > 0);
    return initialSearch || searchTerm?.length || activeFilters;
  }

  protected onInit() {
    const includeSynonyms = this.config.getBoolean(ResultListProp.IncludeSynonyms);
    const initialSearch = this.config.getBoolean(ResultListProp.InitialSearch);
    this._searchConfig = {
      [ResultListProp.IncludeSynonyms]: includeSynonyms,
      [ResultListProp.InitialSearch]: initialSearch,
      [ResultListProp.Columns]: []
    }
    this.includeSynonyms = includeSynonyms;
  }

  fetchLangNames(callback?: (langs: Language[]) => void) {
    this.dictIds = this.sharedStateService.getDictionaryIds();
    this.sourceLangCodes = this.sharedStateService.getSourceLangs();
    this.targetLangCodes = this.sharedStateService.getTargetLangs();
    this.dictService.getLanguages(this.dictIds).subscribe({
      next: (langs) => {
        this.languages = langs;
        callback?.(langs);
        this.performSearch(this.sharedStateService.getPaginationData(), true);
      }
    });
  }

  needToFetchLangNames() {
    return !_.isEqual(this.sharedStateService.getDictionaryIds(), this.dictIds) ||
      !_.isEqual(this.sharedStateService.getSourceLangs(), this.sourceLangCodes) ||
      !_.isEqual(this.sharedStateService.getTargetLangs(), this.targetLangCodes);
  }

  override doUpdate(): void {
    this.logger.trace('BaseResultListComponent.doUpdate:', {})
    super.doUpdate();
    if (this.needToFetchLangNames()) {
      this.fetchLangNames();
    } else {
      this.performSearch(this.sharedStateService.getPaginationData(), true);
    }
  }

  hasNoResults() {
    return this.isSearching && !this.dataSource.data?.length;
  }

  resultClick(res: TermSearchResult, index?: number, skipUrl?: boolean): void {
    const conceptId = res.term.conceptId;
    const termId = res.term.id;
    const cid = res.cid;
    this.sharedStateService.selectConcept({conceptId, cid, termId, index});
  }

  selectConcept(selection: ConceptSelection) {
    this.sharedStateService.selectConcept(selection);
  }

  getSessionObj(): SessionObject {
    return this.sharedStateService.sessionObj;
  }

  private openDetailsForSingleResult() {
    if (this.shouldOpenSingleResult() && this.results?.length === 1) {
      const res = this.results[0];
      this.resultClick(res);
    }
  }

  protected shouldOpenSingleResult = () => false;

  protected validateSortKeys(): void {
    if (!this.results || this.results.length <= 1) return;

    // Group terms by language code
    const termsByLanguage: Record<string, { term: Term, sortKey: string }[]> = {};

    this.results.forEach(result => {
      const term = result.term;
      if (!term.sortKey) return; // Skip if no sortKey

      if (!termsByLanguage[term.languageCode]) {
        termsByLanguage[term.languageCode] = [];
      }

      termsByLanguage[term.languageCode].push({
        term,
        sortKey: term.sortKey
      });
    });

    // Check each language group for duplicate sortKeys with different names
    Object.entries(termsByLanguage).forEach(([langCode, terms]) => {
      // Group by sortKey
      const termsBySortKey: Record<string, Term[]> = {};

      terms.forEach(({term, sortKey}) => {
        if (!termsBySortKey[sortKey]) {
          termsBySortKey[sortKey] = [];
        }
        termsBySortKey[sortKey].push(term);
      });

      // Check for duplicates with different names
      Object.entries(termsBySortKey).forEach(([sortKey, termsWithSameKey]) => {
        if (termsWithSameKey.length > 1) {
          // Create a Map with lowercase names as keys to detect case-insensitive duplicates
          const uniqueNamesMap = new Map<string, Term[]>();

          termsWithSameKey.forEach(term => {
            const lowerName = term.name.toLowerCase();
            if (!uniqueNamesMap.has(lowerName)) {
              uniqueNamesMap.set(lowerName, []);
            }
            uniqueNamesMap.get(lowerName)!.push(term);
          });

          // If we have more than one case-insensitive unique name, log the error
          if (uniqueNamesMap.size > 1) {
            this.logger.error(
              `Duplicate sortKey "${sortKey}" for different terms in language "${langCode}":`,
              termsWithSameKey.map(t => `"${t.name}" (id: ${t.id})`)
            );
          }
        }
      });
    });
  }
}
